import ListWrapper from "../utils/ListWrapper";
import D3EFeature from "./D3EFeature";
import D3EAppTemplate from "./D3EAppTemplate";
import DBObject from "../utils/DBObject";
import CloneContext from "../utils/CloneContext";
import CollectionUtils from "../utils/CollectionUtils";

export default class BuilderProject extends DBObject {
  private static readonly _CONTACT: number = 0;
  private static readonly _EMAIL: number = 1;
  private static readonly _FEATURES: number = 2;
  private static readonly _NAME: number = 3;
  private static readonly _PROJECTDETAILS: number = 4;
  private static readonly _TEMPLATES: number = 5;
  private static readonly _WEBSITE: number = 6;
  public id: number = 0;
  public otherMaster: DBObject;
  private _name: string = "";
  private _email: string = "";
  private _projectDetails: string = "";
  private _contact: string = "";
  private _website: string = "";
  private _features: Array<D3EFeature> = ListWrapper.reference(
    this,
    "features",
    BuilderProject._FEATURES
  );
  private _templates: Array<D3EAppTemplate> = ListWrapper.reference(
    this,
    "templates",
    BuilderProject._TEMPLATES
  );
  public constructor(
    d3eParams?: Partial<{
      contact: string;
      email: string;
      features: Array<D3EFeature>;
      name: string;
      projectDetails: string;
      templates: Array<D3EAppTemplate>;
      website: string;
    }>
  ) {
    super();

    this.setContact(d3eParams?.contact ?? "");

    this.setEmail(d3eParams?.email ?? "");

    this.setFeatures(
      d3eParams?.features ??
        ListWrapper.reference(this, "features", BuilderProject._FEATURES)
    );

    this.setName(d3eParams?.name ?? "");

    this.setProjectDetails(d3eParams?.projectDetails ?? "");

    this.setTemplates(
      d3eParams?.templates ??
        ListWrapper.reference(this, "templates", BuilderProject._TEMPLATES)
    );

    this.setWebsite(d3eParams?.website ?? "");
  }
  public get d3eType(): string {
    return "BuilderProject";
  }
  public clear(): void {
    this.d3eChanges.clear();
  }
  public get name(): string {
    return this._name;
  }
  public setName(val: string): void {
    let isValChanged: boolean = this._name !== val;

    if (!isValChanged) {
      return;
    }

    this.updateD3EChanges(BuilderProject._NAME, this._name);

    this._name = val;

    this.fire("name", this);
  }
  public get email(): string {
    return this._email;
  }
  public setEmail(val: string): void {
    let isValChanged: boolean = this._email !== val;

    if (!isValChanged) {
      return;
    }

    this.updateD3EChanges(BuilderProject._EMAIL, this._email);

    this._email = val;

    this.fire("email", this);
  }
  public get projectDetails(): string {
    return this._projectDetails;
  }
  public setProjectDetails(val: string): void {
    let isValChanged: boolean = this._projectDetails !== val;

    if (!isValChanged) {
      return;
    }

    this.updateD3EChanges(BuilderProject._PROJECTDETAILS, this._projectDetails);

    this._projectDetails = val;

    this.fire("projectDetails", this);
  }
  public get contact(): string {
    return this._contact;
  }
  public setContact(val: string): void {
    let isValChanged: boolean = this._contact !== val;

    if (!isValChanged) {
      return;
    }

    this.updateD3EChanges(BuilderProject._CONTACT, this._contact);

    this._contact = val;

    this.fire("contact", this);
  }
  public get website(): string {
    return this._website;
  }
  public setWebsite(val: string): void {
    let isValChanged: boolean = this._website !== val;

    if (!isValChanged) {
      return;
    }

    this.updateD3EChanges(BuilderProject._WEBSITE, this._website);

    this._website = val;

    this.fire("website", this);
  }
  public get features(): Array<D3EFeature> {
    return this._features;
  }
  public setFeatures(val: Array<D3EFeature>): void {
    let isValChanged: boolean = CollectionUtils.isNotEquals(
      this._features,
      val
    );

    if (!isValChanged) {
      return;
    }

    if (!this.d3eChanges.contains(BuilderProject._FEATURES)) {
      let _old: Array<D3EFeature> = Array.from(this._features);

      this.updateD3EChanges(BuilderProject._FEATURES, _old);
    }

    this.updateObservableColl("features", this._features, val);

    this._features.clear();

    this._features.addAll(val);

    this.fire("features", this);
  }
  public addToFeatures(val: D3EFeature, index: number = -1): void {
    let _old: Array<D3EFeature> = [];

    let _isNewChange: boolean = !this.d3eChanges.contains(
      BuilderProject._FEATURES
    );

    if (_isNewChange) {
      _old = Array.from(this._features);
    }

    if (index === -1) {
      if (!this._features.contains(val)) this._features.add(val);
    } else {
      this._features.remove(this._features.elementAt(index));

      this._features.add(val);
    }

    this.fire("features", this, val, true);

    this.updateObservable("features", null, val);

    if (_isNewChange) {
      this.updateD3EChanges(BuilderProject._FEATURES, _old);
    }
  }
  public removeFromFeatures(val: D3EFeature): void {
    let _old: Array<D3EFeature> = [];

    let _isNewChange: boolean = !this.d3eChanges.contains(
      BuilderProject._FEATURES
    );

    if (_isNewChange) {
      _old = Array.from(this._features);
    }

    this._features.remove(val);

    this.fire("features", this, val, false);

    if (_isNewChange) {
      this.updateD3EChanges(BuilderProject._FEATURES, _old);
    }
  }
  public get templates(): Array<D3EAppTemplate> {
    return this._templates;
  }
  public setTemplates(val: Array<D3EAppTemplate>): void {
    let isValChanged: boolean = CollectionUtils.isNotEquals(
      this._templates,
      val
    );

    if (!isValChanged) {
      return;
    }

    if (!this.d3eChanges.contains(BuilderProject._TEMPLATES)) {
      let _old: Array<D3EAppTemplate> = Array.from(this._templates);

      this.updateD3EChanges(BuilderProject._TEMPLATES, _old);
    }

    this.updateObservableColl("templates", this._templates, val);

    this._templates.clear();

    this._templates.addAll(val);

    this.fire("templates", this);
  }
  public addToTemplates(val: D3EAppTemplate, index: number = -1): void {
    let _old: Array<D3EAppTemplate> = [];

    let _isNewChange: boolean = !this.d3eChanges.contains(
      BuilderProject._TEMPLATES
    );

    if (_isNewChange) {
      _old = Array.from(this._templates);
    }

    if (index === -1) {
      if (!this._templates.contains(val)) this._templates.add(val);
    } else {
      this._templates.remove(this._templates.elementAt(index));

      this._templates.add(val);
    }

    this.fire("templates", this, val, true);

    this.updateObservable("templates", null, val);

    if (_isNewChange) {
      this.updateD3EChanges(BuilderProject._TEMPLATES, _old);
    }
  }
  public removeFromTemplates(val: D3EAppTemplate): void {
    let _old: Array<D3EAppTemplate> = [];

    let _isNewChange: boolean = !this.d3eChanges.contains(
      BuilderProject._TEMPLATES
    );

    if (_isNewChange) {
      _old = Array.from(this._templates);
    }

    this._templates.remove(val);

    this.fire("templates", this, val, false);

    if (_isNewChange) {
      this.updateD3EChanges(BuilderProject._TEMPLATES, _old);
    }
  }
  public get(field: number): any {
    switch (field) {
      case BuilderProject._NAME: {
        return this._name;
      }

      case BuilderProject._EMAIL: {
        return this._email;
      }

      case BuilderProject._PROJECTDETAILS: {
        return this._projectDetails;
      }

      case BuilderProject._CONTACT: {
        return this._contact;
      }

      case BuilderProject._WEBSITE: {
        return this._website;
      }

      case BuilderProject._FEATURES: {
        return this._features;
      }

      case BuilderProject._TEMPLATES: {
        return this._templates;
      }
      default: {
        return null;
      }
    }
  }
  public updateD3EChanges(index: number, value: any): void {
    if (this.lockedChanges()) {
      return;
    }

    super.updateD3EChanges(index, value);
  }
  public restore(): void {
    /*
TODO: Might be removed
*/

    this.d3eChanges.restore(this);
  }
  public deepClone(clearId = true): BuilderProject {
    let ctx: CloneContext = new CloneContext({ "clearId": clearId });

    return ctx.startClone(this);
  }
  public collectChildValues(ctx: CloneContext): void {}
  public deepCloneIntoObj(dbObj: DBObject, ctx: CloneContext): void {
    let obj: BuilderProject = dbObj as BuilderProject;

    obj.id = this.id;

    obj.setName(this._name);

    obj.setEmail(this._email);

    obj.setProjectDetails(this._projectDetails);

    obj.setContact(this._contact);

    obj.setWebsite(this._website);

    obj.setFeatures(this._features);

    obj.setTemplates(this._templates);
  }
  public set(field: number, value: any): void {
    switch (field) {
      case BuilderProject._NAME: {
        this.setName(value as string);
        break;
      }

      case BuilderProject._EMAIL: {
        this.setEmail(value as string);
        break;
      }

      case BuilderProject._PROJECTDETAILS: {
        this.setProjectDetails(value as string);
        break;
      }

      case BuilderProject._CONTACT: {
        this.setContact(value as string);
        break;
      }

      case BuilderProject._WEBSITE: {
        this.setWebsite(value as string);
        break;
      }

      case BuilderProject._FEATURES: {
        this.setFeatures((value as Array<any>).cast<D3EFeature>().toList());
        break;
      }

      case BuilderProject._TEMPLATES: {
        this.setTemplates(
          (value as Array<any>).cast<D3EAppTemplate>().toList()
        );
        break;
      }
    }
  }
}
